import { logWarning } from './logger';

export type VersionCheckerState = {
    lastVersion: string | null;
};

// `lastVersion` used to be module-scoped, which meant two `useVersionChecker()`
// instances shared state: the second instance's first poll silently primed the
// shared value, and the first instance never fired its callback on the actual
// upgrade. Each checker now owns its own state via `createVersionCheckerState`.
// The module-level `latestKnownVersion` stays around purely so Sentry can tag
// spans with the last observed version regardless of which checker saw it.
let latestKnownVersion: string | null = null;
// First successful poll of this SPA session. Used by `isVersionStale` so the
// router can force a hard reload before a navigation would fault on a chunk
// produced by a newer deploy than what this tab booted on.
let initialVersion: string | null = null;

export function createVersionCheckerState(): VersionCheckerState {
    return { lastVersion: null };
}

export async function checkVersion(
    state: VersionCheckerState,
    onVersionUpdate?: () => unknown,
) {
    try {
        const response = await fetch('/version.json', {
            signal: AbortSignal.timeout(5000),
            cache: 'no-store',
        });
        const data = await response.json();

        if (data.version) {
            if (initialVersion == null) {
                initialVersion = data.version;
            }

            if (data.version !== state.lastVersion) {
                if (state.lastVersion != null) {
                    onVersionUpdate?.();
                }
                state.lastVersion = data.version;
                latestKnownVersion = data.version;
            }
        }
    } catch (error) {
        // Polled on a timer — silently swallow expected network/abort failures
        // (offline, flaky connection, tab backgrounded, request timed out). Anything
        // else (e.g. JSON parse on a broken `version.json` deploy) is worth a trail.
        if (isExpectedFetchFailure(error)) {
            return;
        }
        logWarning(`Failed to check version: ${error instanceof Error ? error.message : String(error)}`);
    }
}

function isExpectedFetchFailure(error: unknown): boolean {
    if (!(error instanceof Error)) {
        return false;
    }
    // `AbortSignal.timeout` rejects with a TimeoutError DOMException; fetch network
    // failures surface as TypeError ("Failed to fetch" / "NetworkError") across browsers.
    return (
        error.name === 'AbortError'
        || error.name === 'TimeoutError'
        || error instanceof TypeError
    );
}

export function getLastVersion() {
    return latestKnownVersion ?? 'unknown';
}

// True once the background poller has observed a version different from the
// one the tab originally booted on. Stays true until the next full page load
// reseeds `initialVersion`. The router uses this to bounce a stale tab to a
// fresh document instead of letting it fault on a removed chunk mid-route.
export function isVersionStale(): boolean {
    return (
        initialVersion != null
        && latestKnownVersion != null
        && initialVersion !== latestKnownVersion
    );
}
